/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useMemo } from "react"

import { useDispatch } from "react-redux"

import type { IAppDispatch } from "../store"

import { EModalId, type TModalProps, modalReducerActions } from "./modal.slice"

type TModalDispatcher = {
    showModal<TIncomingModalProps extends Record<string, unknown> = {}>(
        id: EModalId,
        modalProps?: TModalProps<TIncomingModalProps>
    ): void
    hideModal(id: EModalId): void
    refreshModal<TIncomingModalProps extends Record<string, unknown> = {}>(
        id: EModalId,
        modalProps?: TModalProps<TIncomingModalProps>
    ): void
}

function useModalDispatcher(): ReturnType<() => TModalDispatcher> {
    const dispatch: IAppDispatch = useDispatch<IAppDispatch>()

    const showModal: <TIncomingModalProps extends Record<string, unknown> = {}>(
        id: EModalId,
        props: TModalProps<TIncomingModalProps>
    ) => void = useCallback(
        <TIncomingModalProps extends Record<string, unknown> = {}>(
            id: EModalId,
            props: TModalProps<TIncomingModalProps>
        ): void => void dispatch(modalReducerActions.showModal({ id, props })),
        []
    )

    const hideModal: (modalId: EModalId) => void = useCallback(
        (id: EModalId): void => void dispatch(modalReducerActions.hideModal({ id })),
        []
    )

    const refreshModal: <TIncomingModalProps extends Record<string, unknown> = {}>(
        id: EModalId,
        props: TModalProps<TIncomingModalProps>
    ) => void = useCallback(
        <TIncomingModalProps extends Record<string, unknown> = {}>(
            id: EModalId,
            props: TModalProps<TIncomingModalProps>
        ): void => void dispatch(modalReducerActions.refreshModal({ id, props })),
        []
    )

    return useMemo((): TModalDispatcher => ({ showModal, hideModal, refreshModal }), [dispatch])
}

export { useModalDispatcher, type TModalDispatcher }
